import React from 'react';

import { useDynamicIcon } from '@lumapps/assets/hooks/useDynamicIcon';
import { lumappsMicroApp } from '@lumapps/lumx/custom-icons';
import { Emphasis, Tooltip } from '@lumapps/lumx/react';
import { getIconFromType } from '@lumapps/search-results/helpers';
import { useTranslate } from '@lumapps/translations';

import { AskAiSource, AskAiDocumentTypes } from '../../types';
import { formatSearchResultUrl, getAskAiSourceTitle } from '../../utils';
import { AskAiSourceChip as AskAiSourceChipUI } from './AskAiSourceChip';

interface AskAiSourceChipProps {
    source: AskAiSource;
    className?: string;
    /** Callback triggered when the source chip is clicked */
    onClick?: () => void;
}

/**
 * Display source as chip
 * @param source source to be displayed
 * @param className classname to be used for chip
 * @param onClick callback triggered when the chip is clicked
 * @returns a chip for a source with a tooltip, an icon, source title and index
 */
export const AskAiSourceChip = ({ source, className, onClick }: AskAiSourceChipProps) => {
    const { translateKey } = useTranslate();

    const { type, mimeType, url, icon } = source;
    const sourceTitle = getAskAiSourceTitle(source, translateKey);

    const isContent = type === AskAiDocumentTypes.CONTENT;
    const isDirectoryEntryMicroApp = type === AskAiDocumentTypes.DIRECTORY_ENTRY_MICROAPP;

    // We retrieve the icon based on the type of document and add for genericDocument or media a color if applicable.
    const shouldUseMimeType =
        (type === AskAiDocumentTypes.GENERIC_DOCUMENT || type === AskAiDocumentTypes.MEDIA) && mimeType;

    let sourceIcon = '';
    if (isContent && icon) {
        sourceIcon = icon;
    } else if (shouldUseMimeType) {
        sourceIcon = getIconFromType(mimeType);
    } else {
        sourceIcon = getIconFromType(type);
    }
    const { iconPath } = useDynamicIcon({ icon: sourceIcon });

    // icon for directory as microapp is a custom icon so we already have the SVG on our side
    const finalIcon = isDirectoryEntryMicroApp ? lumappsMicroApp : iconPath;

    return (
        <Tooltip label={sourceTitle} placement="top">
            <AskAiSourceChipUI
                className={className}
                leftIcon={finalIcon}
                emphasis={Emphasis.low}
                linkAs="a"
                href={formatSearchResultUrl(url)}
                target="_blank"
                onClick={onClick}
            >
                {sourceTitle}
            </AskAiSourceChipUI>
        </Tooltip>
    );
};
